// ResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes, css } from '@emotion/react';

// ============ ANIMATIONS ============
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeInUp = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-30px); }
`;

// ============ STYLED COMPONENTS ============
const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #f0f9ff 0%, #f8fafc 100%);
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 500ms ease-out;
`;

const BackgroundOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.4;
  pointer-events: none;
  
  ${props => props.$variant === 'primary' && css`
    width: 500px;
    height: 500px;
    background: rgba(14, 165, 233, 0.2);
    top: -100px;
    right: -100px;
    animation: ${float} 20s ease-in-out infinite;
  `}
  
  ${props => props.$variant === 'secondary' && css`
    width: 400px;
    height: 400px;
    background: rgba(139, 92, 246, 0.15);
    bottom: -50px;
    left: -50px;
    animation: ${float} 15s ease-in-out infinite reverse;
  `}
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 10;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
  max-width: 500px;
  width: 100%;
  animation: ${fadeInUp} 600ms cubic-bezier(0.34, 1.56, 0.64, 1);

  @media (max-width: 768px) {
    padding: 40px 24px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const Title = styled.h1`
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 15px;
  color: #64748b;
  line-height: 1.6;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  position: relative;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;

  .requirement {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 400;
    margin-left: 4px;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px 12px 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 200ms;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
    background: #f0f9ff;
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }

  ${props => props.$isPassword && css`
    padding-right: 44px;
  `}

  ${props => props.$hasError && css`
    border-color: #ef4444;
    background: #fef2f2;
  `}
`;

const TogglePasswordButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: color 200ms;

  &:hover {
    color: #64748b;
  }
`;

const PasswordRequirements = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: #64748b;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Requirement = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .icon {
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
    color: white;
    font-size: 10px;
  }

  ${props => props.$met && css`
    color: #059669;

    .icon {
      background: #10b981;
    }
  `}

  ${props => !props.$met && css`
    color: #9ca3af;

    .icon {
      background: #d1d5db;
    }
  `}
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 200ms;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 32px;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(14, 165, 233, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
    animation: ${props => props.$isLoading ? `spin 1s linear infinite` : 'none'};
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background: #ecfdf5;
  border: 1px solid #10b981;
  border-radius: 10px;
  padding: 24px;
  text-align: center;

  .icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .title {
    font-weight: 700;
    color: #047857;
    margin-bottom: 8px;
    font-size: 18px;
  }

  .message {
    color: #059669;
    font-size: 14px;
    margin-bottom: 20px;
  }

  a {
    display: inline-block;
    background: #10b981;
    color: white;
    padding: 10px 24px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;
    font-size: 14px;
    transition: background 200ms;

    &:hover {
      background: #059669;
    }
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #ef4444;
  border-radius: 10px;
  padding: 24px;
  text-align: center;

  .icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .title {
    font-weight: 700;
    color: #dc2626;
    margin-bottom: 8px;
    font-size: 18px;
  }

  .message {
    color: #ef4444;
    font-size: 14px;
    margin-bottom: 20px;
  }

  a {
    display: inline-block;
    background: #ef4444;
    color: white;
    padding: 10px 24px;
    border-radius: 8px;
    text-decoration: none;
    font-weight: 600;
    font-size: 14px;
    transition: background 200ms;

    &:hover {
      background: #dc2626;
    }
  }
`;

const InlineErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 24px;
  color: #dc2626;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// ============ ICON COMPONENTS ============
const Icons = {
  Lock: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  Eye: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  EyeOff: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ),
  Loader: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  ),
  Check: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  AlertCircle: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  CheckCircle: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  ),
};

// ============ RESET PASSWORD COMPONENT ============
const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Password validation
  const passwordRequirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const passwordIsValid =
    passwordRequirements.minLength &&
    passwordRequirements.hasUppercase &&
    passwordRequirements.hasLowercase &&
    passwordRequirements.hasNumber;

  const formIsValid = passwordIsValid && password === confirmPassword && password.length > 0;

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setTokenValid(false);
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/auth/validate-reset-token?token=${token}`,
          { method: 'GET' }
        );

        if (response.ok) {
          setTokenValid(true);
        } else {
          setTokenValid(false);
        }
      } catch (err) {
        console.error('Token validation error:', err);
        setTokenValid(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!formIsValid) {
      setPasswordError('Please meet all password requirements');
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setPasswordError(
          data.message || 'Failed to reset password. Please try again.'
        );
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setPasswordError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <PageContainer>
        <BackgroundOrb $variant="primary" />
        <BackgroundOrb $variant="secondary" />
        <Content>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <Icons.Loader />
            </div>
            <Title>Validating link...</Title>
            <Subtitle>Please wait while we verify your reset link</Subtitle>
          </Card>
        </Content>
      </PageContainer>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <PageContainer>
        <BackgroundOrb $variant="primary" />
        <BackgroundOrb $variant="secondary" />
        <Content>
          <Card>
            <ErrorMessage>
              <div className="icon">⚠️</div>
              <div className="title">Link Expired or Invalid</div>
              <div className="message">
                This password reset link has expired or is invalid. Please request a new one.
              </div>
              <Link to="/forgot-password">Request New Link</Link>
            </ErrorMessage>
          </Card>
        </Content>
      </PageContainer>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <PageContainer>
        <BackgroundOrb $variant="primary" />
        <BackgroundOrb $variant="secondary" />
        <Content>
          <Card>
            <SuccessMessage>
              <div className="icon">✓</div>
              <div className="title">Password Reset Successfully</div>
              <div className="message">
                Your password has been updated. Redirecting to login...
              </div>
              <Link to="/login">Go to Login</Link>
            </SuccessMessage>
          </Card>
        </Content>
      </PageContainer>
    );
  }

  // Form state
  return (
    <PageContainer>
      <BackgroundOrb $variant="primary" />
      <BackgroundOrb $variant="secondary" />

      <Content>
        <Card>
          <Header>
            <IconWrapper>
              <Icons.Lock />
            </IconWrapper>
            <Title>Create New Password</Title>
            <Subtitle>
              Enter your new password. Make sure it's strong and unique.
            </Subtitle>
          </Header>

          <form onSubmit={handleSubmit}>
            {passwordError && (
              <InlineErrorMessage>
                <Icons.AlertCircle />
                {passwordError}
              </InlineErrorMessage>
            )}

            {/* Password Field */}
            <FormGroup>
              <Label htmlFor="password">New Password</Label>
              <PasswordInputWrapper>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  disabled={isLoading}
                  $isPassword
                  autoFocus
                />
                {password && (
                  <TogglePasswordButton
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                  </TogglePasswordButton>
                )}
              </PasswordInputWrapper>

              {password && (
                <PasswordRequirements>
                  <Requirement $met={passwordRequirements.minLength}>
                    <div className="icon">{passwordRequirements.minLength ? '✓' : '—'}</div>
                    At least 8 characters
                  </Requirement>
                  <Requirement $met={passwordRequirements.hasUppercase}>
                    <div className="icon">{passwordRequirements.hasUppercase ? '✓' : '—'}</div>
                    One uppercase letter (A-Z)
                  </Requirement>
                  <Requirement $met={passwordRequirements.hasLowercase}>
                    <div className="icon">{passwordRequirements.hasLowercase ? '✓' : '—'}</div>
                    One lowercase letter (a-z)
                  </Requirement>
                  <Requirement $met={passwordRequirements.hasNumber}>
                    <div className="icon">{passwordRequirements.hasNumber ? '✓' : '—'}</div>
                    One number (0-9)
                  </Requirement>
                </PasswordRequirements>
              )}
            </FormGroup>

            {/* Confirm Password Field */}
            <FormGroup>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInputWrapper>
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  disabled={isLoading}
                  $isPassword
                  $hasError={confirmPassword && password !== confirmPassword}
                />
                {confirmPassword && (
                  <TogglePasswordButton
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                  </TogglePasswordButton>
                )}
              </PasswordInputWrapper>

              {confirmPassword && password !== confirmPassword && (
                <div style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px' }}>
                  ✗ Passwords do not match
                </div>
              )}

              {confirmPassword && password === confirmPassword && (
                <div style={{ color: '#10b981', fontSize: '13px', marginTop: '8px' }}>
                  ✓ Passwords match
                </div>
              )}
            </FormGroup>

            <SubmitButton type="submit" disabled={isLoading || !formIsValid} $isLoading={isLoading}>
              {isLoading ? (
                <>
                  <Icons.Loader />
                  Resetting password...
                </>
              ) : (
                <>
                  <Icons.Check />
                  Reset Password
                </>
              )}
            </SubmitButton>
          </form>
        </Card>
      </Content>
    </PageContainer>
  );
};

export default ResetPassword;
